/**
 * OGrupoPage.tsx — Página "O Grupo" (/o-grupo)
 * Fiel ao Claude Design: Nectaris - O Grupo.dc.html
 * Seções: Header dark, Missão (texto+orbital), Como Atuamos, Framework Analítico,
 * Liderança (experiência), CTA para Iniciativas, Footer
 *
 * ORB-SITE-002: animações via data-reveal / data-reveal-delay
 * gerenciadas pelo useNectarisFx em SiteLayout (sem hook local).
 */

import { NectarisLogoMark } from "../atoms";

/* ============================================================
   HEADER DARK
   ============================================================ */
function GrupoHeader() {
  return (
    <header className="ns-grupo-header" aria-labelledby="grupo-h1">
      <div className="ns-wrap">
        <p className="ns-eyebrow ns-eyebrow--dark">o grupo</p>
        <h1 id="grupo-h1" style={{ color: "#FFFFFF", maxWidth: "22ch" }}>
          Um grupo, uma obsessão:{" "}
          <span className="ns-accent-dark">resultado real.</span>
        </h1>
      </div>
    </header>
  );
}

/* ============================================================
   MISSÃO — texto + diagrama orbital
   ============================================================ */
function GrupoMissao() {
  return (
    <section className="ns-grupo-missao" aria-labelledby="missao-h2">
      <div className="ns-wrap">
        <div className="ns-grupo-missao__inner">
          {/* Coluna texto */}
          <div className="ns-grupo-missao__copy">
            <p className="ns-grupo-missao__tagline" data-reveal>
              A Nectaris nasceu do lado de dentro da operação, não da sala de reunião.
            </p>
            <div className="ns-grupo-missao__body" data-reveal data-reveal-delay="100">
              <p className="ns-grupo-missao__p">
                Fechamento de mês, conciliação bancária, resposta a conselho. Foi nessa
                rotina, em operações de grande porte, que se formou a convicção do grupo:
                relatório bonito não sustenta decisão, número confiável sim.
              </p>
              <p className="ns-grupo-missao__p">
                Tudo o que o grupo constrói, software ou serviço, serve a uma única pergunta:{" "}
                <strong style={{ color: "#0B1B2B" }}>onde está o resultado?</strong> A frente
                de trabalho muda; a régua permanece. Número com origem. Processo sem
                retrabalho. Gestão com ritmo, até a eficiência virar hábito da casa.
              </p>
              <p className="ns-grupo-missao__p">
                E método vence heroísmo: o que funciona é documentado e vira padrão. A
                inteligência construída em cada operação é carregada para a seguinte, e cada
                entrega chega mais rápida e mais precisa que a anterior.
              </p>
            </div>
            <div className="ns-grupo-missao__blockquote" data-reveal data-reveal-delay="180">
              <p>
                Nascemos na operação, medimos tudo pelo resultado e transformamos o que
                funciona em método — é assim que cada entrega chega mais rápida e mais precisa
                que a anterior.
              </p>
            </div>
          </div>

          {/* Coluna orbital */}
          <div className="ns-grupo-missao__orbital" aria-hidden="true">
            <div className="ns-grupo-orbital">
              {/* Anéis orbitais giratórios */}
              <div className="ns-grupo-orbital__ring" />
              <div className="ns-grupo-orbital__ring-inner" />

              {/* Pills orbitando */}
              <div
                className="ns-grupo-orbital__pill"
                style={{ left: "240px", top: "6px" }}
              >
                Consultoria
              </div>
              <div
                className="ns-grupo-orbital__pill"
                style={{ left: "44px", top: "366px" }}
              >
                Automação
              </div>
              <div
                className="ns-grupo-orbital__pill ns-grupo-orbital__pill--dark"
                style={{ left: "436px", top: "366px" }}
              >
                Orbita<span className="ns-grupo-orbital__pill-accent">.</span>
              </div>

              {/* Centro */}
              <div className="ns-grupo-orbital__center">
                <NectarisLogoMark size={64} variant="dark" />
                <span className="ns-grupo-orbital__center-label">RESULTADO REAL</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   COMO ATUAMOS — 4 steps
   ============================================================ */
const COMO_STEPS = [
  {
    num: "01",
    title: "Diagnóstico",
    desc: "Entendemos a operação, os sistemas e onde o resultado escapa. Em dias, não meses.",
    dark: false,
  },
  {
    num: "02",
    title: "Conexão",
    desc: "Ligamos as fontes que existem, como estão. Dado imperfeito entra com etiqueta honesta e melhora mês a mês.",
    dark: false,
  },
  {
    num: "03",
    title: "Gestão",
    desc: "Cadência mensal: leitura dos números, desvios com dono e prazo, pauta que decide.",
    dark: false,
  },
  {
    num: "04",
    title: "Resultado",
    desc: "Caixa sem susto, margem protegida e horas de planilha viradas em horas de decisão.",
    dark: true,
  },
];

function ComoAtuamos() {
  return (
    <section className="ns-como-atuamos" aria-labelledby="como-h2">
      <div className="ns-wrap">
        <p className="ns-eyebrow">como atuamos</p>
        <h2 id="como-h2" data-reveal style={{ maxWidth: "26ch" }}>
          Do diagnóstico ao resultado, num ciclo que não para
        </h2>

        <div
          className="ns-como-atuamos__grid"
          data-reveal
          data-reveal-delay="120"
          aria-label="Ciclo de atuação"
        >
          {COMO_STEPS.map((step) => (
            <article
              key={step.num}
              className={`ns-como-step${step.dark ? " ns-como-step--dark" : " ns-como-step--light"}`}
            >
              <span className="ns-como-step__num">{step.num}</span>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   FRAMEWORK ANALÍTICO — 6 elos
   ============================================================ */
const ELOS = [
  {
    eyebrow: "01 · ESTRUTURAR",
    question: "O que precisamos enxergar, e como o negócio deve ser representado?",
    tags: ["mapear", "padronizar", "classificar", "responsabilizar"],
    flow: "operação → modelo gerencial",
    amber: false,
  },
  {
    eyebrow: "02 · REGISTRAR",
    question: "Estamos capturando corretamente aquilo que acontece?",
    tags: ["capturar", "identificar", "validar", "corrigir"],
    flow: "evento → dado estruturado",
    amber: false,
  },
  {
    eyebrow: "03 · INTEGRAR",
    question: "As partes da operação se conectam numa visão só?",
    tags: ["conectar", "relacionar", "consolidar", "atualizar"],
    flow: "dados dispersos → visão integrada",
    amber: false,
  },
  {
    eyebrow: "04 · GOVERNAR",
    question: "A informação representa a realidade e pode ser rastreada?",
    tags: ["validar", "conciliar", "controlar", "rastrear"],
    flow: "dado → informação confiável",
    amber: false,
  },
  {
    eyebrow: "05 · ANALISAR",
    question: "O que aconteceu, por que aconteceu e o que vem aí?",
    tags: ["medir", "comparar", "projetar", "alertar"],
    flow: "informação → insight",
    amber: false,
  },
  {
    eyebrow: "06 · GERIR",
    question: "Quem faz o quê diante do que a informação mostra?",
    tags: ["priorizar", "decidir", "agir", "aprender"],
    flow: "insight → resultado",
    amber: true,
  },
];

function FrameworkAnalitico() {
  return (
    <section className="ns-framework" aria-labelledby="framework-h2">
      <div className="ns-wrap">
        <p className="ns-eyebrow ns-eyebrow--dark">framework de cultura analítica</p>
        <h2 className="ns-framework__h2" id="framework-h2" data-reveal>
          Da operação à gestão analítica
        </h2>
        <p className="ns-framework__lead" data-reveal data-reveal-delay="100">
          Uma cadeia contínua que transforma o que acontece na operação em dado confiável,
          entendimento gerencial e ação. Seis elos, cada um respondendo a uma pergunta que
          toda empresa deveria saber responder.
        </p>

        <div
          className="ns-framework__grid"
          data-reveal
          data-reveal-delay="150"
          aria-label="Seis elos do framework analítico"
        >
          {ELOS.map((elo) => (
            <article
              key={elo.eyebrow}
              className={`ns-elo-card${elo.amber ? " ns-elo-card--amber" : " ns-elo-card--base"}`}
            >
              <span className="ns-elo-card__eyebrow">{elo.eyebrow}</span>
              <p className="ns-elo-card__question">{elo.question}</p>
              <div className="ns-elo-card__tags">
                {elo.tags.map((tag) => (
                  <span
                    key={tag}
                    className={`ns-elo-tag${elo.amber ? " ns-elo-tag--amber" : " ns-elo-tag--cyan"}`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <span className="ns-elo-card__flow">
                {elo.flow.split("→").map((part, i, arr) => (
                  <span key={i}>
                    {part.trim()}
                    {i < arr.length - 1 && (
                      <span className="ns-elo-card__flow-accent"> → </span>
                    )}
                  </span>
                ))}
              </span>
            </article>
          ))}
        </div>

        <div className="ns-framework__summary" data-reveal data-reveal-delay="180">
          <div className="ns-framework__summary-item ns-framework__summary-item--cyan">
            <span className="ns-framework__summary-label">CONSTRUÇÃO<br />DA INFORMAÇÃO</span>
            <p className="ns-framework__summary-desc">
              estruturar → registrar → integrar → governar: o lastro que torna o número
              digno de confiança
            </p>
          </div>
          <div className="ns-framework__summary-item ns-framework__summary-item--amber">
            <span className="ns-framework__summary-label">GERAÇÃO<br />DE VALOR</span>
            <p className="ns-framework__summary-desc">
              analisar → gerir → aprender: a decisão volta para a operação e o ciclo
              recomeça mais forte
            </p>
          </div>
        </div>

        <p className="ns-framework__footer" data-reveal data-reveal-delay="200">
          CULTURA ANALÍTICA É O CICLO INTEIRO FUNCIONANDO SEM PARAR, NÃO UM RELATÓRIO NO FIM DO MÊS
        </p>
      </div>
    </section>
  );
}

/* ============================================================
   LIDERANÇA — tabela de experiência
   ============================================================ */
const EXPERIENCIAS = [
  {
    label: "HOLDINGS",
    desc: "Controladoria de estrutura patrimonial com mais de R$ 19 bilhões e 200 CNPJs sob uma régua única",
  },
  {
    label: "INDÚSTRIA",
    desc: "Gestão de desempenho no chão de fábrica: custo, produtividade e margem por linha",
  },
  {
    label: "BANCOS",
    desc: "Controle e evidência no padrão de quem presta contas a auditoria e a regulador",
  },
  {
    label: "GESTORAS",
    desc: "Operações de fundos e reporte a investidor, com prazo que não se negocia",
  },
  {
    label: "CONSULTORIA",
    desc: "Projetos de grande porte: método documentado, entregável e independente de quem executa",
  },
];

function Lideranca() {
  return (
    <section className="ns-lideranca" aria-labelledby="lideranca-h2">
      <div className="ns-wrap">
        <div className="ns-lideranca__inner">
          {/* Coluna copy */}
          <div className="ns-lideranca__copy">
            <p className="ns-eyebrow">liderança</p>
            <h2 id="lideranca-h2" data-reveal style={{ maxWidth: "20ch" }}>
              Sócios formados em ambientes que não perdoam erro
            </h2>
            <p className="ns-lideranca__p" data-reveal data-reveal-delay="80">
              A trajetória dos sócios passa por holdings patrimoniais, indústrias, bancos,
              gestoras de recursos e consultorias de grande porte. Em cada uma dessas casas,
              a pergunta era a mesma; o que mudava era o preço do erro.
            </p>
            <p className="ns-lideranca__p" data-reveal data-reveal-delay="130">
              Da holding vem a disciplina de consolidar dezenas de empresas sob uma única
              régua. Da indústria, a leitura de custo e produtividade no detalhe. Do banco e
              da gestora, o padrão de evidência de quem responde a auditor e a investidor. Da
              consultoria, o método que sobrevive à troca de pessoas.
            </p>
            <p className="ns-lideranca__p" data-reveal data-reveal-delay="180">
              É esse repertório que a Nectaris leva para empresas em crescimento, no tamanho e
              no ritmo que elas suportam.
            </p>
          </div>

          {/* Tabela de experiência */}
          <div
            className="ns-lideranca__table"
            data-reveal
            data-reveal-delay="100"
            aria-label="Experiência da liderança por setor"
          >
            {EXPERIENCIAS.map((exp) => (
              <div key={exp.label} className="ns-lideranca__row">
                <span className="ns-lideranca__row-label">{exp.label}</span>
                <p className="ns-lideranca__row-desc">{exp.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   CTA PARA INICIATIVAS + FOOTER
   ============================================================ */
function GrupoCta() {
  return (
    <section className="ns-grupo-cta" aria-label="Chamada para iniciativas">
      <div className="ns-grupo-cta__inner">
        <h2 className="ns-grupo-cta__h2" data-reveal>
          Conheça as iniciativas que compõem o grupo
        </h2>
        <a href="/iniciativas" className="ns-btn-primary" data-reveal data-reveal-delay="120">
          Ver iniciativas →
        </a>
      </div>

      <footer className="ns-footer" aria-label="Rodapé Nectaris">
        <div className="ns-footer__inner">
          <a href="/" className="ns-footer__brand" aria-label="Nectaris — início">
            <NectarisLogoMark size={30} variant="dark" />
            <span className="ns-footer__wordmark">NECTARIS</span>
          </a>
          <span className="ns-footer__copy">© 2026 NECTARIS · BRASIL</span>
        </div>
      </footer>
    </section>
  );
}

/* ============================================================
   EXPORT
   ============================================================ */
export function OGrupoPage() {
  return (
    <>
      <GrupoHeader />
      <GrupoMissao />
      <ComoAtuamos />
      <FrameworkAnalitico />
      <Lideranca />
      <GrupoCta />
    </>
  );
}
